/**
 * ============================================================================
 * SHOPPING.JS v34.0 - WEEKLY MENU BACKEND PERSISTENZ
 * ============================================================================
 *
 * ✅ v34.0: WEEKLY MENU BACKEND PERSISTENZ
 *           - Menü der Woche wird beim Schließen des Dropdowns gespeichert
 *           - ID basiert auf Kalenderwoche (YYYY-WNN)
 *           - Automatisches Laden beim Start
 *           - API: /api/weekly-menus
 *
 * ✅ v33.0: MENÜ DER WOCHE + INFO MODAL
 *           - "Menü der Woche" Sektion
 *           - Info-Button (ⓘ) mit statischer Modul-Beschreibung
 *           - View Toggle (Grid/Liste) in Shopping statt ListOverview
 *           - Neues Layout: 3 Reihen (Header, Controls, Menü)
 *
 * ✅ v32.0: MOBILE DESIGN GUIDE
 *           - isMobile State mit Resize-Listener
 *           - Responsive Container (padding: 0 auf Mobile)
 *           - Touch-Targets min 44px
 *           - Einheitliche Abstände (8px Basis)
 *
 * ✅ v31.0: PWA OFFLINE SUPPORT
 *           - IndexedDB Caching für Offline-Nutzung
 *           - Sync-Queue für Offline-Änderungen
 *           - Offline-Indikator und Fallback
 *
 * ✅ v30.0: KALENDER-INTEGRATION
 *           - Einkaufslisten können zum Kalender hinzugefügt werden
 *           - sourceShoppingListId für Verknüpfung
 *           - createCalendarEntryForShoppingList() Funktion
 *
 * ✅ v29.0: TOKEN CONTEXT - Verwendet zentrales Token-Management
 * ✅ v28.5: LISTDETAIL PROPS FIX
 * ✅ v28.2: ENDPOINT FIX (Backend v1.6.0 kompatibel)
 * ✅ v28.0: ANALYTICS TRACKING
 *
 * @version 34.0.0 - Weekly Menu Backend Persistenz
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToken } from '../../context/TokenContext'; // ✅ v29: NEU!
import { useOffline } from '../../context/OfflineContext'; // ✅ v31: PWA Offline
import { useTranslation } from '../../context/I18nContext';
import { indexedDBService } from '../../services/indexedDB.service'; // ✅ v31: IndexedDB

import Loading, { 
  LoadingIndicator, 
  Skeleton, 
  SkeletonCard, 
  SkeletonGrid,
  EmptyState 
} from '../common/Loading';

import ListOverview from './ListOverview';
import ListDetail from './ListDetail';
import CreateListModal from './CreateListModal';
import './Shopping.css';

// ✅ v30.0: Kalender-Cache Import für Invalidierung
import { invalidateCalendarCache } from '../calendar/calendarCache';

// ============================================================================
// ✅ v28.2: API CONFIG - KORREKTE ENDPOINTS (Backend v1.6.0)
// ============================================================================
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const LOADING_DELAY_MS = 200;

console.log('🛒 Shopping.js v34.0 loaded - WEEKLY MENU BACKEND PERSISTENZ 🍽️');

/**
 * ✅ v28: Safe Track Function - nutzt /track/batch
 */
const trackAnalyticsEvent = async (token, eventName, metadata = {}) => {
  if (!token || !eventName) return;
  
  try {
    const event = {
      type: 'event',
      event_name: String(eventName).substring(0, 100),
      component: String(metadata.component || 'Shopping').substring(0, 100),
      metadata: metadata,
      timestamp: new Date().toISOString()
    };
    
    const response = await fetch(`${API_BASE_URL}/api/analytics/track/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ events: [event] })
    });
    
    if (response.ok) {
      console.log(`✅ Analytics tracked: ${eventName}`, metadata);
    } else {
      console.warn(`❌ Analytics failed: ${response.status}`, eventName);
    }
  } catch (err) {
    console.warn(`❌ Analytics error: ${eventName}`, err.message);
  }
};

// ============================================================================
// GLOBALER STATE + SHARED PROMISE
// ============================================================================
let GLOBAL_LISTS_CACHE = [];
let GLOBAL_HAS_LOADED = false;
let GLOBAL_LOADING_PROMISE = null;

// ============================================================================
// HELPER
// ============================================================================
const normalizeList = (list) => {
  if (!list) return list;

  return {
    ...list,
    id: list.id,
    // ✅ v28.2: Backend v1.6.0 liefert beide Formate
    assignedTo: list.assignedTo ?? list.assigned_to ?? 'alle',
    dueDate: list.dueDate ?? list.due_date ?? null,
    createdAt: list.createdAt ?? list.created_at,
    updatedAt: list.updatedAt ?? list.updated_at,
    items: Array.isArray(list.items) ? list.items : []
  };
};

/**
 * ✅ v30.0: Erstellt Kalendereintrag für Einkaufsliste
 * - sourceShoppingListId: Verknüpft Kalendereintrag mit Einkaufsliste
 * - Emoji: 🛒 für Einkaufslisten-Termine
 */
const createCalendarEntryForShoppingList = async (token, listData) => {
  console.log('📅 createCalendarEntryForShoppingList called with:', listData);

  if (!token) {
    console.warn('📅 No token for calendar entry creation');
    return { success: false, error: 'Kein Token verfügbar' };
  }

  if (!listData || !listData.name) {
    console.warn('📅 No list data or name');
    return { success: false, error: 'Keine Listendaten vorhanden' };
  }

  if (!listData.dueDate) {
    console.warn('📅 No due date for calendar entry');
    return { success: false, error: 'Kein Datum für Kalendereintrag' };
  }

  try {
    // Datum normalisieren
    let appointmentDate = listData.dueDate;
    if (appointmentDate.includes('T')) {
      appointmentDate = appointmentDate.split('T')[0];
    }

    // Uhrzeit: aus calendarTime oder Fallback 10:00
    const appointmentTime = listData.calendarTime || '10:00';

    // Artikel-Anzahl für Beschreibung
    const itemCount = listData.items?.length || 0;
    const itemSummary = itemCount > 0
      ? `\n\n📦 ${itemCount} Artikel auf der Liste`
      : '';

    const calendarData = {
      title: `🛒 ${listData.name}`,
      description: (listData.description || `Einkaufsliste: ${listData.name}`) + itemSummary,
      date: appointmentDate,
      time: appointmentTime,
      category: 'einkaufen',  // Kategorie für Einkaufen
      person: listData.assignedTo || 'alle',
      allDay: false,
      // ✅ v30.0: Shopping-List-ID für Verknüpfung
      sourceShoppingListId: listData.id || null,
      isCompleted: false
    };

    console.log('📅 Creating calendar entry for shopping list:', calendarData.title);
    console.log('📅 Date:', calendarData.date, 'Time:', calendarData.time);
    console.log('📅 Source Shopping List ID:', calendarData.sourceShoppingListId);

    const response = await fetch(`${API_BASE_URL}/api/calendar`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(calendarData)
    });

    if (response.ok) {
      const result = await response.json();
      console.log('✅ Calendar entry created:', result.appointment?.id, 'linked to shopping list:', calendarData.sourceShoppingListId);

      // Kalender-Cache invalidieren
      invalidateCalendarCache();

      return { success: true, appointmentId: result.appointment?.id };
    } else {
      const errorData = await response.json();
      console.warn('⚠️ Calendar entry creation failed:', errorData.error || response.status);
      return { success: false, error: errorData.error || 'Unbekannter Fehler' };
    }
  } catch (err) {
    console.error('❌ Calendar entry creation error:', err.message);
    return { success: false, error: err.message };
  }
};

// ============================================================================
// COMPONENT
// ============================================================================
const Shopping = ({ active, externalLists = [] }) => {
  
  // ✅ v29: useLogto NUR für isAuthenticated, NICHT für getAccessToken!
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();

  // ✅ v29: Token aus zentralem Context!
  const { token, loading: tokenLoading } = useToken();

  // ✅ v31: Offline-Status aus Context
  const { isOnline, addToQueue } = useOffline();

  const { t } = useTranslation();
  
  // ============================================================================
  // STATE
  // ============================================================================
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [lists, setLists] = useState(() => GLOBAL_LISTS_CACHE);
  const [isLoading, setIsLoading] = useState(!GLOBAL_HAS_LOADED);
  const [showLoading, setShowLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(!GLOBAL_HAS_LOADED);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);


  const [selectedListId, setSelectedListId] = useState(null);
  const [selectedListData, setSelectedListData] = useState(null);

  // ✅ v34: Menü der Woche - MIT BACKEND PERSISTENZ
  const [weeklyMenu, setWeeklyMenu] = useState({
    montag: '',
    dienstag: '',
    mittwoch: '',
    donnerstag: '',
    freitag: '',
    samstag: '',
    sonntag: ''
  });
  const [currentWeekId, setCurrentWeekId] = useState(null);
  const [liveWeekId, setLiveWeekId] = useState(null); // Die aktuelle Kalenderwoche
  const [weeklyMenuLoaded, setWeeklyMenuLoaded] = useState(false);
  const [showWeeklyMenu, setShowWeeklyMenu] = useState(false);
  const [menuHistory, setMenuHistory] = useState([]);
  const [showWeekPicker, setShowWeekPicker] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // ✅ v33: View toggle in Shopping
  const [showInfo, setShowInfo] = useState(false); // ✅ v33: Info Modal
  
  // ============================================================================
  // REFS
  // ============================================================================
  const loadingTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const hasTrackedViewRef = useRef(false);

  // ============================================================================
  // ✅ v32: Mobile Responsive State
  // ============================================================================
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // FAB Event
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.module === 'shopping') {
        setShowCreateModal(true);
      }
    };
    window.addEventListener('planivio-create-new', handler);
    return () => window.removeEventListener('planivio-create-new', handler);
  }, []);

  // ============================================================================
  // ✅ v34: WEEKLY MENU - Laden beim Start
  // ============================================================================
  const loadWeeklyMenu = useCallback(async (weekId) => {
    if (!token) return;

    try {
      const url = weekId
        ? `${API_BASE_URL}/api/weekly-menus/${weekId}`
        : `${API_BASE_URL}/api/weekly-menus`;
      console.log('🍽️ Loading weekly menu...', weekId || 'current');
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const loadedWeekId = data.weekId || data.id;
        console.log('✅ Weekly menu loaded:', loadedWeekId);
        setCurrentWeekId(loadedWeekId);
        if (!weekId) setLiveWeekId(loadedWeekId); // Aktuelle KW merken
        setWeeklyMenu({
          montag: data.montag || '',
          dienstag: data.dienstag || '',
          mittwoch: data.mittwoch || '',
          donnerstag: data.donnerstag || '',
          freitag: data.freitag || '',
          samstag: data.samstag || '',
          sonntag: data.sonntag || ''
        });
        setWeeklyMenuLoaded(true);
      }
    } catch (err) {
      console.warn('⚠️ Weekly menu load failed:', err.message);
    }
  }, [token]);

  const loadMenuHistory = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/weekly-menus/history?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMenuHistory(data.menus || []);
      }
    } catch (err) {
      console.warn('⚠️ Menu history load failed:', err.message);
    }
  }, [token]);

  // ✅ v34: Weekly Menu laden wenn Token verfügbar
  useEffect(() => {
    if (token && !weeklyMenuLoaded) {
      loadWeeklyMenu();
    }
  }, [token, weeklyMenuLoaded, loadWeeklyMenu]);

  // History laden wenn Picker geöffnet wird
  useEffect(() => {
    if (showWeekPicker && token) {
      loadMenuHistory();
    }
  }, [showWeekPicker, token, loadMenuHistory]);

  // ============================================================================
  // ✅ v34: WEEKLY MENU - Speichern beim Schließen des Dropdowns
  // ============================================================================
  const saveWeeklyMenu = useCallback(async () => {
    if (!token || !currentWeekId) return;

    try {
      console.log('💾 Saving weekly menu for:', currentWeekId);
      const response = await fetch(`${API_BASE_URL}/api/weekly-menus/${currentWeekId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(weeklyMenu)
      });

      if (response.ok) {
        console.log('✅ Weekly menu saved');
        // Analytics direkt aufrufen (trackEvent noch nicht definiert)
        trackAnalyticsEvent(token, 'weekly_menu_save', {
          component: 'Shopping',
          weekId: currentWeekId
        });
      } else {
        console.warn('⚠️ Weekly menu save failed:', response.status);
      }
    } catch (err) {
      console.error('❌ Weekly menu save error:', err.message);
    }
  }, [token, currentWeekId, weeklyMenu]);

  // ✅ v34: Speichern wenn Dropdown geschlossen wird
  const handleToggleWeeklyMenu = useCallback(() => {
    if (showWeeklyMenu) {
      saveWeeklyMenu();
    }
    setShowWeeklyMenu(!showWeeklyMenu);
  }, [showWeeklyMenu, saveWeeklyMenu]);

  // Wochen-Navigation
  const switchWeek = useCallback(async (weekId) => {
    await saveWeeklyMenu();
    await loadWeeklyMenu(weekId);
    setShowWeekPicker(false);
  }, [saveWeeklyMenu, loadWeeklyMenu]);

  // ============================================================================
  // ✅ v29: Track Mount/Unmount + Analytics
  // ============================================================================
  useEffect(() => {
    isMountedRef.current = true;
    console.log('🔄 Shopping MOUNTED');

    return () => {
      isMountedRef.current = false;
      console.log('🔄 Shopping UNMOUNTING');
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, []);

  // ✅ v29: Analytics tracken wenn Token verfügbar (aus Context!)
  useEffect(() => {
    if (token && !hasTrackedViewRef.current) {
      hasTrackedViewRef.current = true;
      console.log('🔐 Shopping: Using token from TokenContext');
      trackAnalyticsEvent(token, 'component_view', { 
        component: 'Shopping',
        listsCount: GLOBAL_LISTS_CACHE.length
      });
    }
  }, [token]);

  // ============================================================================
  // ✅ v29: ANALYTICS HELPER - Verwendet Token aus Context
  // ============================================================================
  const trackEvent = useCallback(async (eventName, metadata = {}) => {
    if (!token) {
      console.warn('❌ No token available for analytics');
      return;
    }
    
    trackAnalyticsEvent(token, eventName, {
      component: 'Shopping',
      ...metadata
    });
  }, [token]);

  useEffect(() => {
    if (lists.length > 0) {
      GLOBAL_LISTS_CACHE = lists;
    }
  }, [lists]);

  // ============================================================================
  // ✅ v29: FETCH FUNCTION - Verwendet Token aus Context
  // ============================================================================
  const fetchListsFromBackend = useCallback(async () => {
    console.log('📡 Fetching lists from backend...');
    
    // ✅ v29: Token aus Context verwenden
    if (!token) {
      throw new Error('Kein Token verfügbar');
    }
    
    // ✅ v28.2: KORREKT - /api/shopping (OHNE /lists!)
    const response = await fetch(`${API_BASE_URL}/api/shopping`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Backend Error ${response.status}`);
    }

    const data = await response.json();
    
    // ✅ v28.2: Backend v1.6.0 liefert ARRAY direkt (nicht {lists: [...]})
    let listsArray = [];
    if (Array.isArray(data)) {
      listsArray = data;
    } else if (data.lists && Array.isArray(data.lists)) {
      listsArray = data.lists;
    } else if (data.success && data.lists) {
      listsArray = data.lists;
    }
    
    const normalizedLists = listsArray.map(normalizeList);
    console.log('✅ Fetched', normalizedLists.length, 'lists');
    return normalizedLists;
  }, [token]);

  // ============================================================================
  // LOAD LISTS - ✅ v31: Mit Offline-Support
  // ============================================================================
  const loadLists = useCallback(async (forceRefresh = false) => {
    // ✅ v31: Wenn offline, aus IndexedDB laden
    if (!isOnline) {
      console.log('📴 Offline mode - loading from IndexedDB...');
      try {
        const cachedLists = await indexedDBService.getShoppingLists();
        if (cachedLists.length > 0) {
          console.log('📦 Loaded', cachedLists.length, 'lists from IndexedDB (offline)');
          if (isMountedRef.current) {
            setLists(cachedLists);
            setIsLoading(false);
            setIsInitialLoad(false);
          }
          return;
        }
      } catch (err) {
        console.warn('⚠️ IndexedDB read failed:', err.message);
      }
      // Fallback auf globalen Cache
      if (GLOBAL_LISTS_CACHE.length > 0) {
        console.log('📦 Using in-memory cache (offline):', GLOBAL_LISTS_CACHE.length, 'lists');
        if (isMountedRef.current) {
          setLists(GLOBAL_LISTS_CACHE);
          setIsLoading(false);
          setIsInitialLoad(false);
        }
        return;
      }
      // Keine Daten verfügbar
      if (isMountedRef.current) {
        setError('Offline - keine gecachten Daten verfügbar');
        setIsLoading(false);
        setIsInitialLoad(false);
      }
      return;
    }

    // ✅ v29: Warten auf Token aus Context
    if (!token) {
      console.log('⏳ Waiting for token from TokenContext...');
      return;
    }

    if (GLOBAL_HAS_LOADED && GLOBAL_LISTS_CACHE.length > 0 && !forceRefresh) {
      console.log('📦 Using cached data:', GLOBAL_LISTS_CACHE.length, 'lists');
      if (isMountedRef.current) {
        setLists(GLOBAL_LISTS_CACHE);
        setIsLoading(false);
        setIsInitialLoad(false);
      }
      return;
    }
    
    if (GLOBAL_LOADING_PROMISE) {
      console.log('⏳ Waiting for existing load to complete...');
      try {
        const cachedLists = await GLOBAL_LOADING_PROMISE;
        console.log('✅ Got data from shared promise:', cachedLists.length, 'lists');
        if (isMountedRef.current) {
          setLists(cachedLists);
          setIsLoading(false);
          setIsInitialLoad(false);
        }
      } catch (err) {
        console.error('❌ Shared promise failed:', err);
        if (isMountedRef.current) {
          setError(err.message);
          setIsLoading(false);
          setIsInitialLoad(false);
        }
      }
      return;
    }
    
    console.log('🚀 Starting new load...');
    
    if (isMountedRef.current) {
      setIsLoading(true);
      setError(null);
      
      if (!isInitialLoad) {
        loadingTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setShowLoading(true);
          }
        }, LOADING_DELAY_MS);
      }
    }
    
    GLOBAL_LOADING_PROMISE = fetchListsFromBackend();
    
    try {
      const normalizedLists = await GLOBAL_LOADING_PROMISE;

      GLOBAL_LISTS_CACHE = normalizedLists;
      GLOBAL_HAS_LOADED = true;

      console.log('✅ Load complete, cache updated:', GLOBAL_LISTS_CACHE.length, 'lists');

      // ✅ v31: In IndexedDB speichern für Offline-Nutzung
      try {
        await indexedDBService.saveShoppingLists(normalizedLists);
        console.log('💾 Shopping lists saved to IndexedDB');
      } catch (dbErr) {
        console.warn('⚠️ IndexedDB save failed:', dbErr.message);
      }

      if (isMountedRef.current) {
        setLists(normalizedLists);
        setIsInitialLoad(false);
      }
      
    } catch (err) {
      console.error('❌ Load error:', err.message);
      
      trackEvent('shopping_load_error', { error: err.message });
      
      if (isMountedRef.current) {
        setError(err.message);
        setIsInitialLoad(false);
        
        if (GLOBAL_LISTS_CACHE.length > 0) {
          setLists(GLOBAL_LISTS_CACHE);
        }
      }
    } finally {
      GLOBAL_LOADING_PROMISE = null;
      
      if (isMountedRef.current) {
        setIsLoading(false);
        setShowLoading(false);
      }
      
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    }
  }, [isInitialLoad, fetchListsFromBackend, trackEvent, token, isOnline]);

  // ============================================================================
  // EVENT LISTENER FÜR REZEPT-SYNC
  // ============================================================================
  useEffect(() => {
    const handleShoppingUpdate = (event) => {
      console.log('📡 Shopping update received from recipe:', event.detail);
      
      trackEvent('shopping_recipe_sync', { 
        source: 'recipe_modal',
        detail: event.detail 
      });
      
      GLOBAL_HAS_LOADED = false;
      GLOBAL_LOADING_PROMISE = null;
      
      loadLists(true);
    };
    
    window.addEventListener('shopping-list-updated', handleShoppingUpdate);
    console.log('✅ Shopping list update listener registered');
    
    return () => {
      window.removeEventListener('shopping-list-updated', handleShoppingUpdate);
      console.log('🧹 Shopping list update listener removed');
    };
  }, [loadLists, trackEvent]);

  // ============================================================================
  // INITIAL LOAD - ✅ v29: Wartet auf Token aus Context
  // ============================================================================
  useEffect(() => {
    if (GLOBAL_HAS_LOADED && GLOBAL_LISTS_CACHE.length > 0) {
      console.log('📦 Using global cache on mount:', GLOBAL_LISTS_CACHE.length, 'lists');
      setLists(GLOBAL_LISTS_CACHE);
      setIsLoading(false);
      setIsInitialLoad(false);
      return;
    }
    
    if (isAuthLoading || tokenLoading) {
      console.log('⏳ Auth/Token loading...');
      return;
    }
    
    if (!isAuthenticated) {
      console.log('🔒 Not authenticated');
      setIsLoading(false);
      setIsInitialLoad(false);
      setError('Bitte anmelden');
      return;
    }
    
    // ✅ v29: Warten auf Token aus Context
    if (!token) {
      console.log('⏳ Waiting for token from TokenContext...');
      return;
    }
    
    console.log('🎬 Starting initial load');
    loadLists();
  }, [isAuthenticated, isAuthLoading, tokenLoading, token, loadLists]);

  // ============================================================================
  // LIST HANDLERS
  // ============================================================================
  const handleSelectList = useCallback((list) => {
    if (String(list.id).startsWith('temp-')) {
      console.log('⚠️ Opening list with temp ID - may cause save issues');
      
      const realList = GLOBAL_LISTS_CACHE.find(l => 
        l.name === list.name && 
        !String(l.id).startsWith('temp-')
      );
      
      if (realList) {
        console.log('✅ Found real version of list:', realList.id);
        const listCopy = JSON.parse(JSON.stringify(realList));
        setSelectedListId(realList.id);
        setSelectedListData(listCopy);
        
        trackEvent('shopping_list_open', { 
          list_id: realList.id,
          items_count: realList.items?.length || 0,
          priority: realList.priority
        });
        return;
      }
    }
    
    console.log('📋 Opening list:', list.id);
    const listCopy = JSON.parse(JSON.stringify(list));
    setSelectedListId(list.id);
    setSelectedListData(listCopy);
    
    trackEvent('shopping_list_open', { 
      list_id: list.id,
      items_count: list.items?.length || 0,
      priority: list.priority
    });
  }, [trackEvent]);

  const handleCloseList = useCallback(() => {
    if (selectedListId) {
      trackEvent('shopping_list_close', { list_id: selectedListId });
    }
    
    setSelectedListId(null);
    setSelectedListData(null);
  }, [selectedListId, trackEvent]);

  // ============================================================================
  // ✅ v31: HANDLE UPDATE LIST - Mit Offline-Support
  // ============================================================================
  const handleUpdateList = useCallback(async (listId, updates) => {
    console.log('📝 Updating list:', listId, updates);

    // Optimistic UI Update
    setSelectedListData(prev => {
      if (!prev) return prev;
      return { ...prev, ...updates };
    });

    const updateFn = (prev) => prev.map(list =>
      list.id === listId ? { ...list, ...updates } : list
    );

    setLists(updateFn);
    GLOBAL_LISTS_CACHE = updateFn(GLOBAL_LISTS_CACHE);

    if (updates.items) {
      const checkedCount = updates.items.filter(i => i.checked).length;
      trackEvent('shopping_items_update', {
        list_id: listId,
        total_items: updates.items.length,
        checked_items: checkedCount
      });
    }

    const currentList = GLOBAL_LISTS_CACHE.find(l => l.id === listId);
    if (!currentList) {
      console.error('❌ List not found in cache:', listId);
      return;
    }

    const mergedList = { ...currentList, ...updates };

    // ✅ v31: Wenn offline, in IndexedDB + Sync-Queue speichern
    if (!isOnline) {
      console.log('📴 Offline - queueing update for:', listId);
      try {
        // IndexedDB aktualisieren
        await indexedDBService.saveShoppingLists(GLOBAL_LISTS_CACHE);
        console.log('💾 Updated list saved to IndexedDB');

        // Zur Sync-Queue hinzufügen
        await addToQueue({
          type: 'shopping',
          operation: 'update',
          endpoint: `/api/shopping/${listId}`,
          method: 'PUT',
          payload: {
            name: mergedList.name,
            description: mergedList.description,
            items: mergedList.items,
            priority: mergedList.priority,
            assignedTo: mergedList.assignedTo,
            dueDate: mergedList.dueDate
          }
        });
        console.log('📤 Update queued for sync');
      } catch (err) {
        console.warn('⚠️ Offline queue failed:', err.message);
      }
      return;
    }

    // Online: Direkt zum Backend senden
    try {
      if (!token) {
        console.error('❌ No token available');
        return;
      }

      // ✅ v28.2: KORREKT - /api/shopping/{id} (OHNE /lists!)
      const response = await fetch(`${API_BASE_URL}/api/shopping/${listId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: mergedList.name,
          description: mergedList.description,
          items: mergedList.items,
          priority: mergedList.priority,
          assignedTo: mergedList.assignedTo,
          dueDate: mergedList.dueDate
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Update failed:', response.status, errorText);
      } else {
        console.log('✅ Backend confirmed update');
        // IndexedDB auch aktualisieren
        try {
          await indexedDBService.saveShoppingLists(GLOBAL_LISTS_CACHE);
        } catch (dbErr) {
          console.warn('⚠️ IndexedDB update failed:', dbErr.message);
        }
      }
    } catch (err) {
      console.error('❌ Update error:', err);
    }
  }, [trackEvent, token, isOnline, addToQueue]);

  // ============================================================================
  // ✅ v31: HANDLE SAVE AND CLOSE - Mit Offline-Support
  // ============================================================================
  const handleSaveAndClose = useCallback(async (updatedList) => {
    // ✅ v28.4: Fallback auf selectedListId wenn updatedList.id undefined/null ist
    let realId = updatedList?.id || selectedListId;

    console.log('💾 Saving list:', realId, '(from updatedList.id:', updatedList?.id, ', selectedListId:', selectedListId, ')');

    if (!realId) {
      console.error('❌ No list ID available!');
      setError('Fehler: Keine Listen-ID gefunden');
      return;
    }

    // ✅ v31: Bei Offline nur lokale Änderungen speichern (temp IDs erlaubt)
    if (!isOnline && String(realId).startsWith('temp-')) {
      console.log('📴 Offline with temp ID - saving locally only');
      const normalizedUpdate = normalizeList({ ...updatedList, id: realId });

      const updateFn = (prev) => prev.map(list =>
        list.id === realId ? normalizedUpdate : list
      );

      setLists(updateFn);
      GLOBAL_LISTS_CACHE = updateFn(GLOBAL_LISTS_CACHE);

      // In IndexedDB speichern
      try {
        await indexedDBService.saveShoppingLists(GLOBAL_LISTS_CACHE);
        console.log('💾 Saved to IndexedDB (offline)');
      } catch (err) {
        console.warn('⚠️ IndexedDB save failed:', err.message);
      }

      setSelectedListId(null);
      setSelectedListData(null);
      return;
    }

    if (String(realId).startsWith('temp-')) {
      console.log('⚠️ Temp ID detected, searching for real ID...');

      const cachedList = GLOBAL_LISTS_CACHE.find(list =>
        list.name === updatedList.name &&
        !String(list.id).startsWith('temp-')
      );

      if (cachedList) {
        realId = cachedList.id;
        console.log('✅ Found real ID:', realId);
      } else {
        console.log('⏳ Real ID not found yet, waiting...');

        for (let i = 0; i < 30; i++) {
          await new Promise(resolve => setTimeout(resolve, 100));

          const freshList = GLOBAL_LISTS_CACHE.find(list =>
            list.name === updatedList.name &&
            !String(list.id).startsWith('temp-')
          );

          if (freshList) {
            realId = freshList.id;
            console.log('✅ Found real ID after waiting:', realId);
            break;
          }
        }

        if (String(realId).startsWith('temp-')) {
          console.error('❌ Could not find real ID for list');
          setError('Liste wird noch erstellt, bitte kurz warten...');
          return;
        }
      }
    }

    const normalizedUpdate = normalizeList({ ...updatedList, id: realId });

    const updateFn = (prev) => prev.map(list =>
      (list.id === realId || list.id === updatedList.id) ? normalizedUpdate : list
    );

    setLists(updateFn);
    GLOBAL_LISTS_CACHE = updateFn(GLOBAL_LISTS_CACHE);

    setSelectedListId(null);
    setSelectedListData(null);

    trackEvent('shopping_list_save', {
      list_id: realId,
      items_count: updatedList.items?.length || 0,
      checked_count: updatedList.items?.filter(i => i.checked).length || 0
    });

    // ✅ v31: Wenn offline, in Sync-Queue speichern
    if (!isOnline) {
      console.log('📴 Offline - queueing save for:', realId);
      try {
        await indexedDBService.saveShoppingLists(GLOBAL_LISTS_CACHE);
        console.log('💾 Saved to IndexedDB');

        await addToQueue({
          type: 'shopping',
          operation: 'update',
          endpoint: `/api/shopping/${realId}`,
          method: 'PUT',
          payload: {
            name: updatedList.name,
            description: updatedList.description,
            items: updatedList.items,
            priority: updatedList.priority,
            assignedTo: updatedList.assignedTo,
            dueDate: updatedList.dueDate
          }
        });
        console.log('📤 Save queued for sync');
      } catch (err) {
        console.warn('⚠️ Offline queue failed:', err.message);
      }
      return;
    }

    // Online: Direkt zum Backend senden
    try {
      if (!token) {
        console.error('❌ No token available');
        return;
      }

      // ✅ v28.2: KORREKT - /api/shopping/{id} (OHNE /lists!)
      const response = await fetch(`${API_BASE_URL}/api/shopping/${realId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: updatedList.name,
          description: updatedList.description,
          items: updatedList.items,
          priority: updatedList.priority,
          assignedTo: updatedList.assignedTo,
          dueDate: updatedList.dueDate
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Backend confirmed save');
        const normalizedData = normalizeList(data);

        const finalUpdate = (prev) => prev.map(list =>
          list.id === normalizedData.id ? normalizedData : list
        );

        setLists(finalUpdate);
        GLOBAL_LISTS_CACHE = finalUpdate(GLOBAL_LISTS_CACHE);

        // IndexedDB aktualisieren
        try {
          await indexedDBService.saveShoppingLists(GLOBAL_LISTS_CACHE);
        } catch (dbErr) {
          console.warn('⚠️ IndexedDB save failed:', dbErr.message);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Save failed:', response.status, errorText);

        trackEvent('shopping_list_save_error', { error: `${response.status}` });

        throw new Error(`Save failed: ${response.status} - ${errorText}`);
      }
    } catch (err) {
      console.error('❌ Save error:', err);
      setError('Speichern fehlgeschlagen');
    }
  }, [trackEvent, selectedListId, token, isOnline, addToQueue]);

  // ============================================================================
  // ✅ v31: CREATE LIST - Mit Offline-Support und Kalender-Integration
  // ============================================================================
  const handleCreateList = useCallback(async (newList) => {
    console.log('➕ Creating list:', newList.name);
    // ✅ v30.0: Log Kalender-Option
    console.log('📅 Add to calendar:', newList.addToCalendar, 'Time:', newList.calendarTime);

    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const optimisticList = {
      id: tempId,
      name: newList.name,
      description: newList.description || null,
      assignedTo: newList.assignedTo || 'alle',
      priority: newList.priority || 'normal',
      dueDate: newList.dueDate || null,
      items: newList.items || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      _isTemp: true,
      _offlineCreated: !isOnline  // ✅ v31: Markiere Offline-Erstellungen
    };

    const addFn = (prev) => [optimisticList, ...prev];

    setLists(addFn);
    GLOBAL_LISTS_CACHE = addFn(GLOBAL_LISTS_CACHE);

    console.log('📊 Optimistic add, cache now:', GLOBAL_LISTS_CACHE.length, 'lists');

    setShowCreateModal(false);

    trackEvent('shopping_list_create', {
      priority: newList.priority || 'normal',
      has_due_date: !!newList.dueDate,
      initial_items: newList.items?.length || 0,
      add_to_calendar: !!newList.addToCalendar,
      offline: !isOnline  // ✅ v31: Track Offline-Erstellung
    });

    // ✅ v31: Wenn offline, in IndexedDB + Sync-Queue speichern
    if (!isOnline) {
      console.log('📴 Offline - saving to IndexedDB and queueing create');
      try {
        // IndexedDB aktualisieren
        await indexedDBService.saveShoppingLists(GLOBAL_LISTS_CACHE);
        console.log('💾 New list saved to IndexedDB');

        // Zur Sync-Queue hinzufügen
        await addToQueue({
          type: 'shopping',
          operation: 'create',
          endpoint: '/api/shopping',
          method: 'POST',
          payload: {
            tempId: tempId,  // Für spätere ID-Zuordnung
            name: newList.name,
            description: newList.description || null,
            assignedTo: newList.assignedTo || 'alle',
            priority: newList.priority || 'normal',
            dueDate: newList.dueDate || null,
            items: newList.items || [],
            addToCalendar: newList.addToCalendar,
            calendarTime: newList.calendarTime
          }
        });
        console.log('📤 Create queued for sync');
      } catch (err) {
        console.warn('⚠️ Offline queue failed:', err.message);
      }
      return;  // Offline: Kein Backend-Call
    }

    // Online: Direkt zum Backend senden
    try {
      if (!token) {
        throw new Error('Kein Token verfügbar');
      }

      // ✅ v28.2: KORREKT - /api/shopping (OHNE /lists!)
      const response = await fetch(`${API_BASE_URL}/api/shopping`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newList.name,
          description: newList.description || null,
          assignedTo: newList.assignedTo || 'alle',
          priority: newList.priority || 'normal',
          dueDate: newList.dueDate || null,
          items: newList.items || []
        })
      });

      const data = await response.json();

      // ✅ v28.2: Backend v1.6.0 liefert das Objekt direkt
      if (response.ok && data.id) {
        console.log('✅ Backend confirmed, ID:', data.id);

        const normalizedData = normalizeList(data);
        normalizedData._isTemp = false;
        normalizedData._offlineCreated = false;

        const replaceFn = (prev) => prev.map(list =>
          list.id === tempId ? normalizedData : list
        );

        setLists(replaceFn);
        GLOBAL_LISTS_CACHE = replaceFn(GLOBAL_LISTS_CACHE);

        // IndexedDB aktualisieren
        try {
          await indexedDBService.saveShoppingLists(GLOBAL_LISTS_CACHE);
        } catch (dbErr) {
          console.warn('⚠️ IndexedDB save failed:', dbErr.message);
        }

        // ✅ v30.0: Kalendereintrag erstellen wenn gewünscht
        if (newList.addToCalendar && newList.dueDate) {
          console.log('📅 Creating calendar entry for shopping list...');

          const calendarResult = await createCalendarEntryForShoppingList(token, {
            ...newList,
            id: data.id  // Echte ID verwenden!
          });

          if (calendarResult.success) {
            console.log('✅ Calendar entry created successfully');
          } else {
            console.warn('⚠️ Calendar entry failed:', calendarResult.error);
          }

          trackEvent('shopping_list_calendar_created', {
            success: calendarResult.success,
            list_id: data.id
          });
        }
      } else {
        throw new Error(data.error || 'Create failed');
      }
    } catch (err) {
      console.error('❌ Create error:', err);

      trackEvent('shopping_list_create_error', { error: err.message });

      const removeFn = (prev) => prev.filter(list => list.id !== tempId);
      setLists(removeFn);
      GLOBAL_LISTS_CACHE = removeFn(GLOBAL_LISTS_CACHE);

      setError('Erstellen fehlgeschlagen: ' + err.message);
    }
  }, [trackEvent, token, isOnline, addToQueue]);

  // ============================================================================
  // ✅ v31: DELETE LIST - Mit Offline-Support
  // ============================================================================
  const handleDeleteList = useCallback(async (listId) => {
    console.log('🗑️ Deleting list:', listId);

    const deletedList = lists.find(list => list.id === listId);
    const deletedIndex = lists.findIndex(list => list.id === listId);

    // Optimistic UI Update
    const removeFn = (prev) => prev.filter(list => list.id !== listId);

    setLists(removeFn);
    GLOBAL_LISTS_CACHE = removeFn(GLOBAL_LISTS_CACHE);

    if (selectedListId === listId) {
      setSelectedListId(null);
      setSelectedListData(null);
    }

    trackEvent('shopping_list_delete', {
      list_id: listId,
      items_count: deletedList?.items?.length || 0,
      offline: !isOnline
    });

    // ✅ v31: Wenn offline, in IndexedDB + Sync-Queue speichern
    if (!isOnline) {
      console.log('📴 Offline - queueing delete for:', listId);
      try {
        // IndexedDB aktualisieren
        await indexedDBService.saveShoppingLists(GLOBAL_LISTS_CACHE);
        console.log('💾 Deleted list removed from IndexedDB');

        // Nur zur Sync-Queue hinzufügen wenn keine temp-ID (bereits synced)
        if (!String(listId).startsWith('temp-')) {
          await addToQueue({
            type: 'shopping',
            operation: 'delete',
            endpoint: `/api/shopping/${listId}`,
            method: 'DELETE',
            payload: { listId }
          });
          console.log('📤 Delete queued for sync');
        } else {
          console.log('ℹ️ Temp list - no sync needed');
        }
      } catch (err) {
        console.warn('⚠️ Offline queue failed:', err.message);
      }
      return;
    }

    // Online: Direkt zum Backend senden
    try {
      if (!token) {
        throw new Error('Kein Token verfügbar');
      }

      // ✅ v28.2: KORREKT - /api/shopping/{id} (OHNE /lists!)
      const response = await fetch(`${API_BASE_URL}/api/shopping/${listId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        console.log('✅ Deleted from backend');
        // IndexedDB auch aktualisieren
        try {
          await indexedDBService.saveShoppingLists(GLOBAL_LISTS_CACHE);
        } catch (dbErr) {
          console.warn('⚠️ IndexedDB update failed:', dbErr.message);
        }
      } else {
        throw new Error('Delete failed');
      }
    } catch (err) {
      console.error('❌ Delete error:', err);

      trackEvent('shopping_list_delete_error', { error: err.message });

      // Rollback
      if (deletedList) {
        const restoreFn = (prev) => {
          const newLists = [...prev];
          newLists.splice(deletedIndex, 0, deletedList);
          return newLists;
        };

        setLists(restoreFn);
        GLOBAL_LISTS_CACHE = restoreFn(GLOBAL_LISTS_CACHE);

        setError('Löschen fehlgeschlagen');
      }
    }
  }, [lists, selectedListId, trackEvent, token, isOnline, addToQueue]);

  // ============================================================================
  // REFRESH HANDLERS
  // ============================================================================
  const handleRetry = useCallback(() => {
    console.log('🔄 Manual retry');
    
    trackEvent('shopping_retry', { type: 'manual' });
    
    GLOBAL_HAS_LOADED = false;
    GLOBAL_LOADING_PROMISE = null;
    setError(null);
    setIsInitialLoad(true);
    loadLists(true);
  }, [loadLists, trackEvent]);

  const handleSilentRefresh = useCallback(() => {
    console.log('🔄 Silent refresh');
    
    trackEvent('shopping_refresh', { type: 'silent' });
    
    GLOBAL_HAS_LOADED = false;
    GLOBAL_LOADING_PROMISE = null;
    loadLists(true);
  }, [loadLists, trackEvent]);

  const handleOpenCreateModal = useCallback(() => {
    trackEvent('shopping_modal_open', { mode: 'create' });
    setShowCreateModal(true);
  }, [trackEvent]);

  // ============================================================================
  // STYLES
  // ============================================================================
  const styles = {
    container: {
      padding: isMobile ? '5px' : '2rem 1.25rem',
      maxWidth: isMobile ? '100%' : '1400px',
      margin: '0 auto',
      width: '100%',
      background: '#E8F4F8',
      minHeight: 'calc(100vh - 100px)',
      borderRadius: isMobile ? '0' : '20px',
      boxSizing: 'border-box',
      overflowX: 'hidden'
    },
    contentWrapper: {
      opacity: showLoading ? 0.7 : 1,
      transition: 'opacity 0.2s ease',
      pointerEvents: showLoading ? 'none' : 'auto'
    },
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: isMobile ? '0.75rem' : '1.5rem',
      padding: isMobile ? '0.75rem' : '0',
      flexWrap: 'wrap',
      gap: isMobile ? '0.75rem' : '1rem'
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flex: 1
    },
    headerIcon: {
      width: isMobile ? '44px' : '48px',
      height: isMobile ? '44px' : '48px',
      background: 'linear-gradient(135deg, #4A90A4, #6BA893)',
      borderRadius: isMobile ? '10px' : '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '1.3rem' : '1.5rem',
      boxShadow: '0 4px 12px rgba(74, 144, 164, 0.3)'
    },
    headerTitle: {
      margin: 0,
      color: '#1F2937',
      fontSize: isMobile ? '1.3rem' : '1.5rem',
      fontWeight: 700,
      flex: 1
    },
    controlsRow: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '1rem',
      padding: isMobile ? '0 0.75rem' : '0'
    },
    headerSubtitle: {
      margin: 0,
      color: '#6B7280',
      fontSize: '0.875rem',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    headerActions: {
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'center'
    },
    refreshBtn: {
      padding: isMobile ? '0.75rem' : '0.625rem',
      background: 'white',
      color: '#6B7280',
      border: '2px solid #E5E7EB',
      borderRadius: isMobile ? '10px' : '12px',
      fontSize: '1rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '44px',
      minHeight: '44px',
      transition: 'all 0.2s ease'
    },
    errorBanner: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      background: '#FEF2F2',
      border: '2px solid #FECACA',
      borderRadius: '12px',
      marginBottom: '1rem',
      color: '#991B1B'
    },
    errorCloseBtn: {
      background: 'none',
      border: 'none',
      fontSize: '1.25rem',
      cursor: 'pointer',
      color: '#991B1B'
    },
    errorContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      background: '#FEF2F2',
      borderRadius: '16px',
      border: '2px solid #FECACA',
      gap: '1rem',
      color: '#991B1B'
    },
    retryBtn: {
      padding: '0.75rem 1.5rem',
      background: 'linear-gradient(135deg, #4A90A4, #6BA893)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontWeight: 600,
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(74, 144, 164, 0.3)'
    },
    syncIndicator: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '0.75rem',
      color: '#4A90A4'
    },

    // ✅ v33: Menü der Woche Styles
    weeklyMenuSection: {
      marginBottom: '1rem',
      padding: isMobile ? '0 0.75rem' : '0'
    },
    weeklyMenuToggle: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      width: '100%',
      padding: '0.875rem 1rem',
      background: 'white',
      border: '2px solid #B8D4DC',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: 600,
      color: '#1F2937',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    weeklyMenuContent: {
      marginTop: '0.75rem',
      padding: '1rem',
      background: 'white',
      border: '2px solid #B8D4DC',
      borderRadius: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem'
    },
    weeklyMenuDay: {
      display: 'flex',
      alignItems: isMobile ? 'flex-start' : 'center',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '0.25rem' : '1rem'
    },
    weeklyMenuLabel: {
      width: isMobile ? '100%' : '100px',
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#4A90A4',
      flexShrink: 0
    },
    weeklyMenuInput: {
      flex: 1,
      width: isMobile ? '100%' : 'auto',
      padding: '0.625rem 0.875rem',
      border: '1px solid #D4E8EC',
      borderRadius: '8px',
      fontSize: '0.875rem',
      color: '#1F2937',
      background: '#F0F9FF',
      outline: 'none',
      transition: 'all 0.2s'
    },

    // ✅ v33.1: Info Button — Kalender-Design (44px Desktop, 32px Mobile)
    infoBtn: {
      width: isMobile ? '32px' : '44px',
      height: isMobile ? '32px' : '44px',
      background: isMobile ? 'transparent' : '#F8FAFC',
      border: isMobile ? 'none' : '1px solid #E2E8F0',
      borderRadius: '12px',
      fontSize: isMobile ? '1rem' : '1.25rem',
      color: '#475569',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
      marginLeft: isMobile ? '0.25rem' : '0.5rem',
      flexShrink: 0,
      boxSizing: 'border-box',
      position: 'relative',
      padding: 0,
      lineHeight: 1
    },
    infoModal: {
      background: 'white',
      border: '2px solid #B8D4DC',
      borderRadius: '16px',
      marginBottom: '1rem',
      padding: isMobile ? '0.75rem' : '0',
      marginLeft: isMobile ? '0.75rem' : '0',
      marginRight: isMobile ? '0.75rem' : '0',
      boxShadow: '0 4px 20px rgba(74, 144, 164, 0.15)',
      overflow: 'hidden'
    },
    infoModalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem 1.25rem',
      background: 'linear-gradient(135deg, #4A90A4, #6BA893)',
      color: 'white'
    },
    infoModalTitle: {
      fontSize: '1rem',
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    infoCloseBtn: {
      background: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      borderRadius: '8px',
      width: '32px',
      height: '32px',
      fontSize: '1rem',
      color: 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s'
    },
    infoModalContent: {
      padding: '1.25rem'
    },
    infoText: {
      margin: '0 0 1rem 0',
      color: '#4B5563',
      fontSize: '0.9rem',
      lineHeight: 1.6
    },
    infoList: {
      margin: 0,
      padding: '0 0 0 1.25rem',
      color: '#6B7280',
      fontSize: '0.875rem',
      lineHeight: 1.8
    }
  };

  // ============================================================================
  // RENDER: SKELETON
  // ============================================================================
  if ((isInitialLoad || tokenLoading) && lists.length === 0 && !error) {
    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div style={styles.headerLeft}>
            <Skeleton width={48} height={48} borderRadius={12} />
            <div>
              <Skeleton width={150} height={24} style={{ marginBottom: 8 }} />
              <Skeleton width={80} height={16} />
            </div>
          </div>
          <Skeleton width={140} height={40} borderRadius={12} />
        </div>
        <SkeletonGrid count={4} cardHeight={180} />
      </div>
    );
  }

  // ============================================================================
  // RENDER: AUTH LOADING
  // ============================================================================
  if (isAuthLoading) {
    return (
      <div style={styles.container}>
        <Loading icon="🔐" text="Authentifizierung..." />
      </div>
    );
  }

  // ============================================================================
  // RENDER: ERROR
  // ============================================================================
  if (error && lists.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <span style={{ fontSize: '3rem' }}>⚠️</span>
          <p style={{ margin: 0, fontWeight: 600 }}>{error}</p>
          <button style={styles.retryBtn} onClick={handleRetry}>
            🔄 Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: MAIN
  // ============================================================================
  return (
    <div style={styles.container}>
      
      {showLoading && (
        <LoadingIndicator text="Aktualisiere..." position="fixed" />
      )}
      
      {error && lists.length > 0 && (
        <div style={styles.errorBanner}>
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)} style={styles.errorCloseBtn}>✕</button>
        </div>
      )}
      
      <div style={styles.contentWrapper}>
        
        {/* ROW 1: Icon + Title + Info Button */}
        <div className="shopping-header-row1">
          <div className="shopping-header-left">
            <div className="shopping-header-icon">🛒</div>
            <div className="shopping-header-title-wrap">
              <h1 className="shopping-header-title">{t('shopping.title')}</h1>
              <span className="shopping-header-subtitle">{lists.length} Listen</span>
            </div>
            <button
              className="shopping-info-btn"
              onClick={() => setShowInfo(!showInfo)}
              title="Modul-Info"
            >
              ⓘ
            </button>
          </div>
        </div>

        {/* Info Modal */}
        {showInfo && (
          <div style={styles.infoModal}>
            <div style={styles.infoModalHeader}>
              <span style={styles.infoModalTitle}>Einkaufslisten - Info</span>
              <button
                style={styles.infoCloseBtn}
                onClick={() => setShowInfo(false)}
              >
                ✕
              </button>
            </div>
            <div style={styles.infoModalContent}>
              <p style={styles.infoText}>
                <strong>Einkaufslisten-Modul</strong> ermöglicht dir das einfache Verwalten
                deiner Einkäufe. Erstelle Listen, füge Artikel hinzu, markiere erledigte
                Produkte und teile Listen mit deiner Familie.
              </p>
              <ul style={styles.infoList}>
                <li>Erstelle mehrere Einkaufslisten</li>
                <li>Füge Artikel per Text oder Sprache hinzu</li>
                <li>Sortiere nach Kategorien und Priorität</li>
                <li>Teile Listen mit Familienmitgliedern</li>
                <li>Synchronisiere mit dem Kalender</li>
                <li>Plane dein Wochenmenü</li>
              </ul>
            </div>
          </div>
        )}

        {/* ROW 2: View Toggle (Toggle-Gruppe wie Rezepte) */}
        <div style={styles.controlsRow}>
          <div className="view-mode-toggle">
            <button
              className={`view-mode-btn${viewMode === 'grid' ? ' active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              ▦ Karten
            </button>
            <button
              className={`view-mode-btn${viewMode === 'list' ? ' active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              ☰ Liste
            </button>
          </div>
        </div>

        {/* ROW 3: Menü der Woche */}
        <div style={styles.weeklyMenuSection}>
          <button
            style={styles.weeklyMenuToggle}
            onClick={handleToggleWeeklyMenu}
          >
            <span>🍽️</span>
            <span>Menü der Woche {currentWeekId ? `(${currentWeekId})` : ''}</span>
            {currentWeekId && liveWeekId && currentWeekId !== liveWeekId && (
              <span style={{ fontSize: '0.7rem', background: '#f59e0b', color: 'white', borderRadius: 4, padding: '2px 6px', marginLeft: 4 }}>Archiv</span>
            )}
            <span style={{ marginLeft: 'auto', fontSize: '0.8rem' }}>{showWeeklyMenu ? '▲' : '▼'}</span>
          </button>

          {showWeeklyMenu && (
            <div style={styles.weeklyMenuContent}>
              {/* Wochen-Navigation */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 8 }}>
                <button
                  onClick={() => {
                    if (!currentWeekId) return;
                    const [y, w] = currentWeekId.split('-W').map(Number);
                    const prevW = w - 1;
                    const prevId = prevW < 1 ? `${y - 1}-W52` : `${y}-W${String(prevW).padStart(2, '0')}`;
                    switchWeek(prevId);
                  }}
                  style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: '0.85rem' }}
                >
                  ← Vorherige
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--primary)' }}>
                    {currentWeekId || '...'}
                  </span>
                  {currentWeekId !== liveWeekId && (
                    <button
                      onClick={() => switchWeek(liveWeekId)}
                      style={{ background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: '0.75rem' }}
                    >
                      Heute
                    </button>
                  )}
                  <button
                    onClick={() => setShowWeekPicker(!showWeekPicker)}
                    style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: '0.8rem' }}
                    title="Gespeicherte Wochen"
                  >
                    📋
                  </button>
                </div>

                <button
                  onClick={() => {
                    if (!currentWeekId) return;
                    const [y, w] = currentWeekId.split('-W').map(Number);
                    const nextW = w + 1;
                    const nextId = nextW > 52 ? `${y + 1}-W01` : `${y}-W${String(nextW).padStart(2, '0')}`;
                    switchWeek(nextId);
                  }}
                  style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: '0.85rem' }}
                >
                  Nächste →
                </button>
              </div>

              {/* History Picker */}
              {showWeekPicker && menuHistory.length > 0 && (
                <div style={{ background: '#f8fafc', borderRadius: 8, padding: 8, marginBottom: 12, maxHeight: 200, overflowY: 'auto', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 6, fontWeight: 600 }}>Gespeicherte Menüs:</div>
                  {menuHistory.map(m => (
                    <button
                      key={m.id}
                      onClick={() => switchWeek(m.id)}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        background: m.id === currentWeekId ? 'var(--teal-bg)' : 'white',
                        border: m.id === currentWeekId ? '1px solid var(--primary)' : '1px solid #e2e8f0',
                        borderRadius: 6, padding: '6px 10px', marginBottom: 4, cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      <strong>{m.id}</strong>
                      {m.id === liveWeekId && <span style={{ fontSize: '0.7rem', color: 'var(--primary)', marginLeft: 6 }}>aktuelle Woche</span>}
                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {[m.montag, m.dienstag, m.mittwoch].filter(Boolean).join(' · ') || 'Leer'}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Tages-Eingaben */}
              {Object.entries(weeklyMenu).map(([day, menu]) => (
                <div key={day} style={styles.weeklyMenuDay}>
                  <label style={styles.weeklyMenuLabel}>
                    {day.charAt(0).toUpperCase() + day.slice(1)}
                  </label>
                  <input
                    type="text"
                    style={styles.weeklyMenuInput}
                    value={menu}
                    onChange={(e) => setWeeklyMenu(prev => ({
                      ...prev,
                      [day]: e.target.value
                    }))}
                    placeholder={`Was gibt's am ${day.charAt(0).toUpperCase() + day.slice(1)}?`}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedListData ? (
          <ListDetail
            key={selectedListId}
            initialData={selectedListData}
            onClose={handleCloseList}
            onSave={handleSaveAndClose}
            onDelete={handleDeleteList}
          />
        ) : lists.length === 0 ? (
          <EmptyState
            icon="🛒"
            title={t('shopping.noLists') || 'Keine Listen'}
            description={t('shopping.createFirstList') || 'Erstelle deine erste Einkaufsliste!'}
            actionText={t('shopping.newList') || 'Neue Liste'}
            onAction={handleOpenCreateModal}
          />
        ) : (
          <ListOverview
            lists={lists}
            onSelectList={handleSelectList}
            onDeleteList={handleDeleteList}
            viewMode={viewMode}
            t={t}
          />
        )}
      </div>


      {showCreateModal && (
        <CreateListModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateList}
          t={t}
        />
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default Shopping;

